import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { formatIsoBrt, todayIsoBrt } from '@/lib/dateBrt';
import { normalizeProjectStatus } from '@/lib/statusNormalize';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  BarChart3, 
  PieChart, 
  Users, 
  FolderOpen,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Printer,
  Calendar
} from 'lucide-react';
import logger from '@/lib/logger';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import logo from '@/assets/logo.png';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Module-level chart options (stable references — avoids per-render re-allocation)
const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
};

const BAR_OPTIONS = {
  ...CHART_OPTIONS,
  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
};

const BAR_OPTIONS_HORIZONTAL = { ...BAR_OPTIONS, indexAxis: 'y' };

function Reports({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  // Filters
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [consultantFilter, setConsultantFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all'); // all | updated | stale
  const [activityDays, setActivityDays] = useState(7);

  // Calculate date ranges
  const getDateRange = (period) => {
    const today = new Date();
    let startDate = null;
    let endDate = formatIsoBrt(today);

    switch (period) {
      case 'last7':
        startDate = formatIsoBrt(new Date(today.setDate(today.getDate() - 7)));
        break;
      case 'last30':
        startDate = formatIsoBrt(new Date(today.setDate(today.getDate() - 30)));
        break;
      case 'thisQuarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        startDate = formatIsoBrt(quarterStart);
        break;
      case 'lastQuarter':
        const lastQuarterEnd = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);
        const lastQuarterStart = new Date(lastQuarterEnd.getFullYear(), lastQuarterEnd.getMonth() - 2, 1);
        startDate = formatIsoBrt(lastQuarterStart);
        endDate = formatIsoBrt(lastQuarterEnd);
        break;
      case 'thisSemester':
        const semesterStart = today.getMonth() < 6 
          ? new Date(today.getFullYear(), 0, 1) 
          : new Date(today.getFullYear(), 6, 1);
        startDate = formatIsoBrt(semesterStart);
        break;
      case 'thisYear':
        startDate = formatIsoBrt(new Date(today.getFullYear(), 0, 1));
        break;
      case 'custom':
        startDate = customStartDate || null;
        endDate = customEndDate || todayIsoBrt();
        break;
      default:
        return { startDate: null, endDate: null };
    }
    return { startDate, endDate };
  };

  // Build the query params used by BOTH data fetch and exports.
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (projectTypeFilter !== 'all') params.append('project_type', projectTypeFilter);
    if (consultantFilter !== 'all') params.append('consultant_name', consultantFilter);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (activityFilter !== 'all') {
      params.append('activity_type', activityFilter);
      params.append('activity_days', String(activityDays || 7));
    }
    const { startDate, endDate } = getDateRange(periodFilter);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectTypeFilter, consultantFilter, periodFilter, customStartDate, customEndDate, statusFilter, activityFilter, activityDays]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      logger.error('Error fetching projects:', error);
    }
  }, []);

  const fetchProjectTypes = useCallback(async () => {
    try {
      const response = await api.get('/project-types');
      // Sort alphabetically
      const sortedTypes = response.data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setProjectTypes(sortedTypes);
    } catch (error) {
      logger.error('Error fetching project types:', error);
    }
  }, []);

  const fetchConsultants = useCallback(async () => {
    try {
      const response = await api.get('/users/consultants');
      setConsultants(response.data);
    } catch (error) {
      logger.error('Error fetching consultants:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/reports/data?${buildParams()}`);
      setData(response.data);
    } catch (error) {
      logger.error('Error fetching report data:', error);
      toast.error('Erro ao carregar dados do relatório');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
    fetchProjects();
    fetchProjectTypes();
    fetchConsultants();
  }, [fetchData, fetchProjects, fetchProjectTypes, fetchConsultants]);

  const handlePrint = () => {
    window.print();
  };

  const downloadBlob = (resp, filename) => {
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportXLSX = async () => {
    setExporting(true);
    try {
      toast.info('Gerando Excel...');
      const resp = await api.get(`/reports/export/xlsx?${buildParams()}`, { responseType: 'blob' });
      downloadBlob(resp, `relatorio_projetos_${todayIsoBrt()}.xlsx`);
      toast.success('Excel exportado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      toast.info('Gerando PDF...');
      const resp = await api.get(`/reports/export/pdf?${buildParams()}`, { responseType: 'blob' });
      downloadBlob(resp, `relatorio_projetos_${todayIsoBrt()}.pdf`);
      toast.success('PDF exportado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  // Chart colors
  const chartColors = {
    green: 'rgba(22, 101, 52, 0.8)',
    blue: 'rgba(30, 64, 175, 0.8)',
    orange: 'rgba(234, 88, 12, 0.8)',
    yellow: 'rgba(202, 138, 4, 0.8)',
    red: 'rgba(220, 38, 38, 0.8)',
    purple: 'rgba(124, 58, 237, 0.8)',
    teal: 'rgba(15, 118, 110, 0.8)',
    gray: 'rgba(107, 114, 128, 0.8)',
  };

  // Projects by Status Chart
  const projectsStatusData = {
    labels: Object.keys(data?.projects_by_status || {}),
    datasets: [{
      data: Object.values(data?.projects_by_status || {}),
      backgroundColor: [chartColors.green, chartColors.blue, chartColors.orange, chartColors.gray],
      borderWidth: 0,
    }]
  };

  // Projects by Type Chart
  const projectsTypeData = {
    labels: Object.keys(data?.projects_by_type || {}),
    datasets: [{
      data: Object.values(data?.projects_by_type || {}),
      backgroundColor: [
        chartColors.green, chartColors.blue, chartColors.orange, 
        chartColors.purple, chartColors.teal, chartColors.yellow, chartColors.red
      ],
      borderWidth: 0,
    }]
  };

  // Tasks by Status Chart
  const tasksStatusData = {
    labels: ['Não Iniciadas', 'Em Andamento', 'Concluídas'],
    datasets: [{
      label: 'Tarefas',
      data: [
        data?.tasks_by_status?.not_started || 0,
        data?.tasks_by_status?.in_progress || 0,
        data?.tasks_by_status?.completed || 0,
      ],
      backgroundColor: [chartColors.gray, chartColors.blue, chartColors.green],
      borderRadius: 6,
    }]
  };

  // Tasks by Consultant Chart
  const consultantNames = Object.keys(data?.tasks_by_consultant || {});
  const tasksConsultantData = {
    labels: consultantNames.slice(0, 8),
    datasets: [
      {
        label: 'Concluídas',
        data: consultantNames.slice(0, 8).map(name => data?.tasks_by_consultant[name]?.completed || 0),
        backgroundColor: chartColors.green,
        borderRadius: 4,
      },
      {
        label: 'Em Andamento',
        data: consultantNames.slice(0, 8).map(name => data?.tasks_by_consultant[name]?.in_progress || 0),
        backgroundColor: chartColors.blue,
        borderRadius: 4,
      },
      {
        label: 'Não Iniciadas',
        data: consultantNames.slice(0, 8).map(name => data?.tasks_by_consultant[name]?.not_started || 0),
        backgroundColor: chartColors.gray,
        borderRadius: 4,
      }
    ]
  };

  // Tasks by Month Chart
  const monthLabels = Object.keys(data?.tasks_by_month || {}).map(m => {
    const [year, month] = m.split('-');
    return new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
  });
  const tasksByMonthData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Total',
        data: Object.values(data?.tasks_by_month || {}).map(m => m.total),
        backgroundColor: chartColors.blue,
        borderRadius: 4,
      },
      {
        label: 'Concluídas',
        data: Object.values(data?.tasks_by_month || {}).map(m => m.completed),
        backgroundColor: chartColors.green,
        borderRadius: 4,
      }
    ]
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-100 text-gray-700',
      'Não Iniciado': 'bg-gray-100 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Em Andamento': 'bg-blue-100 text-blue-700',
      'Iniciado': 'bg-blue-100 text-blue-700',
      'Completed': 'bg-green-100 text-green-700',
      'Concluído': 'bg-green-100 text-green-700',
      'On Hold': 'bg-yellow-100 text-yellow-700',
      'Em Espera': 'bg-yellow-100 text-yellow-700',
      'On Hold - Agency': 'bg-orange-100 text-orange-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" ref={reportRef}>
        {/* Header with Logo for Print */}
        <div className="print:block hidden mb-8">
          <div className="flex items-center justify-between border-b border-gray-300 pb-4">
            <div className="flex items-center gap-4">
              <img src={logo} alt="Logo" className="h-16 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quatro Folhas Consultoria</h1>
                <p className="text-sm text-gray-500">Relatório de Projetos e Atividades</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Gerado em:</p>
              <p className="font-medium">{new Date().toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>
          </div>
        </div>

        {/* Screen Header */}
        <div className="flex items-center justify-between print:hidden">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Relatórios</h1>
            <p className="text-gray-600 mt-1">Visualize métricas e gere relatórios detalhados</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handlePrint}
              className="flex items-center gap-2"
              data-testid="btn-print"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </Button>
            <Button 
              onClick={handleExportXLSX}
              disabled={exporting}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
              data-testid="btn-export-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Excel
            </Button>
            <Button 
              onClick={handleExportPDF}
              disabled={exporting}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
              data-testid="btn-export-pdf"
            >
              <FileText className="w-4 h-4" />
              Exportar PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-gray-200 print:hidden">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Filtros:</span>
            </div>
            
            {/* Period Filter */}
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Período</Label>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="w-48" data-testid="period-filter">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Todos os Períodos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Períodos</SelectItem>
                  <SelectItem value="last7">Últimos 7 dias</SelectItem>
                  <SelectItem value="last30">Últimos 30 dias</SelectItem>
                  <SelectItem value="thisQuarter">Este Trimestre</SelectItem>
                  <SelectItem value="lastQuarter">Último Trimestre</SelectItem>
                  <SelectItem value="thisSemester">Este Semestre</SelectItem>
                  <SelectItem value="thisYear">Este Ano</SelectItem>
                  <SelectItem value="custom">Período Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {periodFilter === 'custom' && (
              <>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-500">Data Inicial</Label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-40"
                    data-testid="custom-start-date"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-500">Data Final</Label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-40"
                    data-testid="custom-end-date"
                  />
                </div>
              </>
            )}

            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Tipo de Projeto</Label>
              <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Todos os Tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  {projectTypes.map((type) => (
                    <SelectItem key={type.type_id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Consultor</Label>
              <Select value={consultantFilter} onValueChange={setConsultantFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Todos Consultores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Consultores</SelectItem>
                  {consultants.map((consultant) => (
                    <SelectItem key={consultant.user_id} value={consultant.full_name}>
                      {consultant.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Status do Projeto</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="Não Iniciado">Não Iniciado</SelectItem>
                  <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                  <SelectItem value="Em Espera">Em Espera</SelectItem>
                  <SelectItem value="Concluído">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Atividade recente</Label>
              <Select value={activityFilter} onValueChange={setActivityFilter}>
                <SelectTrigger className="w-56" data-testid="activity-filter">
                  <SelectValue placeholder="Qualquer atividade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer atividade</SelectItem>
                  <SelectItem value="updated">Atualizados nos últimos X dias</SelectItem>
                  <SelectItem value="stale">Sem atualização há X dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {activityFilter !== 'all' && (
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-gray-500">X (dias)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={activityDays}
                  onChange={(e) => setActivityDays(parseInt(e.target.value, 10) || 1)}
                  className="w-24"
                  data-testid="activity-days"
                />
              </div>
            )}

            <Button variant="outline" size="sm" onClick={() => {
              setProjectTypeFilter('all');
              setConsultantFilter('all');
              setPeriodFilter('all');
              setCustomStartDate('');
              setCustomEndDate('');
              setStatusFilter('all');
              setActivityFilter('all');
              setActivityDays(7);
            }} data-testid="btn-clear-filters">
              Limpar Filtros
            </Button>
          </div>
          
          {/* Active period indicator */}
          {periodFilter !== 'all' && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-emerald-600" />
                <span className="text-gray-600">Período selecionado:</span>
                <Badge className="bg-emerald-100 text-emerald-700">
                  {periodFilter === 'last7' && 'Últimos 7 dias'}
                  {periodFilter === 'last30' && 'Últimos 30 dias'}
                  {periodFilter === 'thisQuarter' && 'Este Trimestre'}
                  {periodFilter === 'lastQuarter' && 'Último Trimestre'}
                  {periodFilter === 'thisSemester' && 'Este Semestre'}
                  {periodFilter === 'thisYear' && 'Este Ano'}
                  {periodFilter === 'custom' && customStartDate && customEndDate && 
                    `${new Date(customStartDate).toLocaleDateString('pt-BR')} - ${new Date(customEndDate).toLocaleDateString('pt-BR')}`
                  }
                  {periodFilter === 'custom' && (!customStartDate || !customEndDate) && 'Selecione as datas'}
                </Badge>
              </div>
            </div>
          )}
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FolderOpen className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data?.totals?.projects || 0}</p>
                <p className="text-sm text-gray-500">Projetos</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data?.totals?.tasks || 0}</p>
                <p className="text-sm text-gray-500">Tarefas</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data?.totals?.consultants || 0}</p>
                <p className="text-sm text-gray-500">Consultores</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FileText className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data?.totals?.licenses || 0}</p>
                <p className="text-sm text-gray-500">Licenças</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
          {/* Projects by Status */}
          <Card className="p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-700" />
              Projetos por Status
            </h3>
            <div className="h-64">
              <Doughnut data={projectsStatusData} options={CHART_OPTIONS} />
            </div>
          </Card>

          {/* Projects by Type */}
          <Card className="p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-700" />
              Projetos por Tipo
            </h3>
            <div className="h-64">
              <Pie data={projectsTypeData} options={CHART_OPTIONS} />
            </div>
          </Card>

          {/* Tasks by Status */}
          <Card className="p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-700" />
              Tarefas por Status
            </h3>
            <div className="h-64">
              <Bar data={tasksStatusData} options={BAR_OPTIONS} />
            </div>
          </Card>

          {/* Tasks by Month */}
          <Card className="p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-700" />
              Tarefas por Mês
            </h3>
            <div className="h-64">
              <Bar data={tasksByMonthData} options={BAR_OPTIONS} />
            </div>
          </Card>
        </div>

        {/* Tasks by Consultant */}
        <Card className="p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" />
            Desempenho por Consultor
          </h3>
          <div className="h-80">
            <Bar data={tasksConsultantData} options={BAR_OPTIONS_HORIZONTAL} />
          </div>
        </Card>

        {/* Licenses Summary */}
        <Card className="p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-yellow-700" />
            Resumo de Licenças
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{data?.licenses_summary?.total || 0}</p>
              <p className="text-sm text-gray-600">Total de Licenças</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-700">{data?.licenses_summary?.valid || 0}</p>
              <p className="text-sm text-green-600">Válidas</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-700">{data?.licenses_summary?.expiring_30_days || 0}</p>
              <p className="text-sm text-yellow-600">Vencendo em 30 dias</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-700">{data?.licenses_summary?.expired || 0}</p>
              <p className="text-sm text-red-600">Vencidas</p>
            </div>
          </div>
        </Card>

        {/* Project Table */}
        <Card className="p-6 border border-gray-200 overflow-hidden">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-emerald-700" />
            Tabela de Projetos
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Projeto</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Tipo</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Progresso</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Fases</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Tarefas</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700 print:hidden">Início</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700 print:hidden">Prazo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.project_table?.map((project) => (
                  <tr key={project.project_id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">{project.name}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{project.client_name}</td>
                    <td className="py-3 px-4 text-gray-600">{project.project_type}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={getStatusColor(project.status)}>{normalizeProjectStatus(project.status)}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-600 rounded-full" 
                            style={{ width: `${project.progress}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{project.progress}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">{project.phases_count}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-green-700">{project.tasks_completed}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-gray-600">{project.tasks_total}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600 print:hidden">
                      {project.start_date ? new Date(project.start_date).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600 print:hidden">
                      {project.end_date ? new Date(project.end_date).toLocaleDateString('pt-BR') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Consultant Performance Table */}
        <Card className="p-6 border border-gray-200 overflow-hidden">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-700" />
            Desempenho por Consultor (Tabela)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Consultor</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Total</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Concluídas</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Em Andamento</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Não Iniciadas</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Taxa de Conclusão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(data?.tasks_by_consultant || {}).map(([name, stats]) => (
                  <tr key={name} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{name}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{stats.total}</td>
                    <td className="py-3 px-4 text-center text-green-700">{stats.completed}</td>
                    <td className="py-3 px-4 text-center text-blue-700">{stats.in_progress}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{stats.not_started}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={stats.completion_rate >= 70 ? 'bg-green-100 text-green-700' : stats.completion_rate >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}>
                        {stats.completion_rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <p>Quatro Folhas Consultoria - Relatório de Projetos</p>
            <p>Desenvolvido por Castle Green</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 1cm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          nav, footer {
            display: none !important;
          }
          main {
            padding: 0 !important;
          }
        }
      `}</style>
    </Layout>
  );
}

export default Reports;
